import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot,} from '@angular/router';
import {BehaviorSubject, combineLatest, interval, of} from 'rxjs';
import {map, switchMap, take, tap} from 'rxjs/operators';
import {DialogImage} from 'src/app/data/dialog-image';
import {Currency} from 'src/app/data/metal-list';
import {Checkout} from 'src/app/model/checkout';
import {PaymentMethod} from 'src/app/model/checkout/credit-card';
import {BasketElement} from 'src/app/model/shop/basket-element';
import {ServerType} from 'src/service/app-init.service';
import {DialogService} from 'src/service/dialog.service';
import {GoldCardService} from 'src/service/gold-card.service';
import {ProductService} from 'src/service/product.service';
import {RestApiService} from 'src/service/rest-api.service';
import {CartService} from '../cart/cart.service';
import {PriceService} from '../price/price.service';
import {MyPackageService} from '../saving-plans/my-package.service';
import {SavingPlansService} from '../saving-plans/saving-plans.service';
import {VaultService} from '../vault/vault.service';
import {UserService} from '../../../service/user.service';
import {AddressService} from '../../../service/address.service';

@Injectable({
  providedIn: "root",
})
export class CheckoutService implements Resolve<any> {
  constructor(
    private productService: ProductService,
    private vaultService: VaultService,
    private cartService: CartService,
    private savingPlanService: SavingPlansService,
    private userService: UserService,
    private addressService: AddressService,
    private myPackageService: MyPackageService,
    private goldCardService: GoldCardService,
    private dialogService: DialogService,
    private restApiService: RestApiService,
    private priceService: PriceService
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    this.id = route.params["id"];
    const type = route.queryParams["type"];
    this.amount = route.queryParams["amount"];
    const paymentRequest = this.productTypeList.find(
      (item) => item.type == type
    );
    this.selectedShipment = null;
    this.selectedPaymentRequest.next(paymentRequest);
    return null;
  }

  amount;
  createSavingPlanTypes = ["savingPlanNew", "savingPlanTresor"];
  redirectToPaymentList = ["savingPlanNew", "savingPlanTresor", "buyForRoll"];
  germanyTranslates = [
    "Duitsland",
    "Germany",
    "Allemagne",
    "Deutschland",
    "Almanya",
  ];
  selectedShipment;
  id;
  pendingSavingsPlanId;
  checkout = new BehaviorSubject<Checkout>(null);
  selectedPaymentRequest = new BehaviorSubject<any>(null);
  totalPrice = new BehaviorSubject<Number>(0);
  shippingPrice = new BehaviorSubject<Number>(0);
  goingBack = false;

  productTypeList = [
    {
      type: "vault", // vault/shipment
      request: () => this.withdrawVaultProduct(),
      routerLink: "/vault",
      afterSuccess: () =>
        this.showSuccessDialog("ORDER_COMPLETED", DialogImage.ORDER_COMPLETED),
      afterCloseDialog: () => this.refreshVault(),
      getShipmentCost: (afterPurchaseType) =>
        this.getVaultShipmentCost(afterPurchaseType),
      getBasketCost: () => of(0),
      getTaxPrice: (afterPurchaseType) => of(0),
      pendingRequest: () => this.vaultService.selectedVaultItem,
      afterFirstSuccess: () => this.showPayAgainDialog("NEED A SUBSCRIPTION", DialogImage.ORDER_COMPLETED),
    },
    {
      type: "cart",
      request: () => this.buyCartProducts(),
      routerLink: "/",
      afterSuccess: () =>
        this.showSuccessDialog("ORDER_COMPLETED", DialogImage.ORDER_COMPLETED),
      afterCloseDialog: () => this.vaultService.getVault(),
      getShipmentCost: (afterPurchaseType) =>
        this.getProductShipmentCost(afterPurchaseType),
      getBasketCost: () => this.getCartBasketCost(),
      getTaxPrice: (afterPurchaseType) =>
        this.productService.getBasketTax(afterPurchaseType),
      pendingRequest: () => this.cartService.buyRequest,
      afterFirstSuccess: () => this.showPayAgainDialog("NEED A CHECKOUT", DialogImage.ORDER_COMPLETED),
    },
    {
      type: "savingPlan",
      request: () => this.withdrawSavingPlan(),
      routerLink: "/saving-plans",
      afterSuccess: () =>
        this.showSuccessDialog("ORDER_COMPLETED", DialogImage.ORDER_COMPLETED),
      afterCloseDialog: () => this.refreshSavingPlans(),
      getShipmentCost: (afterPurchaseType) =>
        this.getSavingPlanShipmentCost(afterPurchaseType),
      getBasketCost: () => this.getGoldCardBasketCost(),
      getTaxPrice: (afterPurchaseType) => of(0),
      pendingRequest: () => this.myPackageService.selectedMyPackage,
    },
    {
      type: "savingPlanProduct",
      request: () => this.withdrawProductSavingPlan(),
      afterSuccess: () =>
        this.showSuccessDialog("ORDER_COMPLETED", DialogImage.ORDER_COMPLETED),
      routerLink: "/saving-plans",
      afterCloseDialog: () => this.refreshSavingPlans(),
      getShipmentCost: (afterPurchaseType) =>
        this.getSavingPlanShipmentCost(afterPurchaseType),
      getBasketCost: () => of(0),
      getTaxPrice: (afterPurchaseType) => of(0),
      pendingRequest: () => this.myPackageService.selectedMyPackage,
    },
    {
      type: "savingPlanNew",
      request: () => this.createSavingPlan(),
      routerLink: "../../",
      queryParams: { showMyPackage: true },
      afterSuccess: () =>
        this.showSuccessDialog("ORDER_COMPLETED", DialogImage.ORDER_COMPLETED),
      afterCloseDialog: () => this.refreshSavingPlans(),
      getBasketCost: () => of(0),
      getTaxPrice: (afterPurchaseType) => of(0),
      pendingRequest: () => this.savingPlanService.selectedSavingPlan,
    },
    {
      type: "savingPlanTresor",
      request: () => this.createTresorSavingPlan(),
      routerLink: "../../",
      queryParams: { showMyPackage: true },
      afterSuccess: () =>
        this.showSuccessDialog("ORDER_COMPLETED", DialogImage.ORDER_COMPLETED),
      afterCloseDialog: () => this.refreshSavingPlans(),
      getBasketCost: () => of(0),
      getTaxPrice: (afterPurchaseType) => of(0),
      pendingRequest: () => this.savingPlanService.selectedSavingPlan,
    },
    {
      type: "buyForRoll",
      request: () => this.buyForRoll(),
      routerLink: "../../../checkout",
      queryParams: { type: "savingPlan" },
      afterSuccess: () =>
        this.showSuccessDialog("GRAM_UPGRADED", DialogImage.GRAM_UPGRADED),
      afterCloseDialog: () => this.refreshSelectedMyPackage(),
      getBasketCost: () => this.getGoldCostForRoll(),
      getTaxPrice: (afterPurchaseType) => of(0),
      pendingRequest: () => this.myPackageService.targetAmountMap,
    },
  ];

  applyPaymentRequest() {
    return this.selectedPaymentRequest.value.request();
  }

  getIdForPaypal(request: any, key: string) {
    return new Promise((resolveOuter) => {
      resolveOuter(
        new Promise((resolveInner) => {
          setTimeout(resolveInner, 100);
        }),
      );
    }).then(() => request[key]);
  }

  refreshVault() {
    this.vaultService.selectedVaultItem.next(null);
    this.vaultService.selectedVaultItemProduct.next(null);
    this.vaultService.withdrawVaultRequest.next(null);
    return this.vaultService.getVault();
  }

  refreshSavingPlans() {
    this.savingPlanService.selectedSavingPlan.next(null);
    this.myPackageService.selectedMyPackage.next(null);
    return interval(2000).pipe(
      take(1),
      switchMap(() =>
        this.savingPlanService.refreshSavingPlansWithoutTakeUntil()
      )
    );
  }

  applyAfterSuccessRequest() {
    const selectedAfterSuccess = this.selectedPaymentRequest.value.afterSuccess;
    if (selectedAfterSuccess) {
      return selectedAfterSuccess();
    } else {
      return of(true);
    }
  }

  showSuccessDialog(text: string, dialogImage?: DialogImage) {
    //"PAY_EURO_SUCCESS"
    return this.dialogService.showSuccessDialog(text, dialogImage);
  }

  showPayAgainDialog(text: string, dialogImage?: DialogImage) {
    //"PAY_EURO_SUCCESS"
    return this.dialogService.showSuccessDialog(text, dialogImage);
  }

  getPaymentToken(creditCard: PaymentMethod) {
    return this.restApiService.post(
      ServerType.STRIPE,
      "create-token",
      creditCard,
      {
        responseType: "text",
      }
    );
  }

  getCreditCards() {
    return this.restApiService.get(ServerType.STRIPE, "credit-cards");
  }

  getPayPalOrder(body: { amount: number; currency: string }): Promise<{ id: string; links: any[] }> {
    return this.restApiService.post(ServerType.STRIPE, "paypal/create-order", body).toPromise();
  }

  getSubscriptionId(): Promise<{ subscriptionId: string } > {
    return this.restApiService.get(ServerType.STRIPE, "paypal/create-subs").toPromise().then(res => {
      return res.subscriptionId;
    });
  }

  sendCheckoutApproved(orderId: string) {
    return this.restApiService.post(ServerType.STRIPE, "paypal-update/order-control", {order_id: orderId});
  }
  sendSubscriptionApproved(subscriptionId: string) {
    return this.restApiService.post(ServerType.STRIPE, "paypal-update/subscription-control", {subscription_id: subscriptionId});
  }
  sendSubscriptionApprovedForUpdate(subscriptionId: string) {
    return this.restApiService.post(ServerType.STRIPE, "paypal-update/subscription-control-update-payment", {subscription_id: subscriptionId});
  }

  refreshCheckout(value: any) {
    this.checkout.next({ ...this.checkout.value, ...value });
  }

  buyCartProducts() {
    return this.productService
      .buy({
        ...this.cartService.buyRequest.value,
        ...this.checkout.value,
        priceJwt: this.priceService.priceJwt.value,
      });
      // .pipe(tap(() => this.cartService.removeAllProductFromCart()));
  }

  buyForRoll() {
    return this.myPackageService.autoRaise(
      this.myPackageService.rollAmount.value,
      this.checkout.value.paymentType
    );
  }

  refreshSelectedMyPackage() {
    this.myPackageService.rollAmount.next(0);
    return this.myPackageService.refreshSelectedMyPackage();
  }

  withdrawVaultProduct() {
    return this.vaultService.withdrawVault({
      basketElements: this.vaultService.basketElements.value,
      ...this.checkout.value,
      priceJwt: this.priceService.priceJwt.value,
    });
  }

  withdrawProductSavingPlan() {
    return this.savingPlanService.withdraw(
      this.myPackageService.selectedMyPackage.value.id,
      this.checkout.value
    );
  }

  withdrawSavingPlan() {
    return this.goldCardService.convertToGoldCard({
      ...this.myPackageService.getWithdrawRequestValue(),
      ...this.checkout.value,
    });
  }

  createSavingPlan() {
    const createRequest = this.savingPlanService.createRequest.value;
    return this.savingPlanService.create(
      this.pendingSavingsPlanId != null
        ? { savingsPlanId: this.pendingSavingsPlanId }
        : { ...createRequest }
    );
  }

  createTresorSavingPlan() {
    const createRequest = this.savingPlanService.createRequest.value;
    return this.savingPlanService.createTresorGoldPlan(
      this.pendingSavingsPlanId != null
        ? { savingsPlanId: this.pendingSavingsPlanId }
        : { ...createRequest }
    );
  }

  getProductShipmentCost(afterPurchaseType) {
    return this.productService
      .getShipmentCost(afterPurchaseType, this.checkout.value?.country)
      .pipe(tap((shippingPrice) => this.shippingPrice.next(shippingPrice)));
  }

  getVaultShipmentCost(afterPurchaseType) {
    return this.vaultService
      .getVaultShipmentCost(afterPurchaseType)
      .pipe(tap((shippingPrice) => this.shippingPrice.next(shippingPrice)));
  }

  getSavingPlanShipmentCost(afterPuchaseType) {
    return this.myPackageService.getSavingPlanShipmentCost(afterPuchaseType);
  }

  getCartBasketCost() {
    return this.cartService.cartListTotalPrice;
  }

  getGoldCostForRoll() {
    return of(this.amount * this.priceService.getPriceItem(Currency.GOLD).buy);
  }

  getGoldCardBasketCost() {
    return this.myPackageService.targetAmountMap.pipe(
      map((goldCardBasketList) =>
        goldCardBasketList.reduce(
          (acc, val) => acc + val.goldCard.formPrice * val.amount,
          0
        )
      )
    );
  }

  getVaultSubscriptionCost() {
    if (
      this.selectedPaymentRequest.value?.type == "cart" &&
      this.selectedShipment == "VAULT"
    ) {
      return this.vaultService.getVaultSubscriptionCost(
        this.cartService.buyRequest.value.basketElements
      );
    } else if (
      this.selectedPaymentRequest.value?.type == "savingPlan" &&
      this.selectedShipment == "VAULT"
    ) {
      return this.getBasketItemListOfGoldCards().pipe(
        switchMap((basketItems) =>
          this.vaultService.getVaultSubscriptionCost(basketItems)
        )
      );
    } else if (
      this.selectedPaymentRequest.value?.type == "savingPlanProduct" &&
      this.selectedShipment == "VAULT"
    ) {
      var currentSavingPlan = this.myPackageService.selectedMyPackage.value;
      var basketItem: BasketElement = {
        productId: currentSavingPlan.productId,
        productName: currentSavingPlan.name,
        quantity: 1,
      };
      return this.vaultService.getVaultSubscriptionCost([basketItem]);
    } else {
      return of(0);
    }
  }

  getBasketItemListOfGoldCards() {
    return this.myPackageService.targetAmountMap.pipe(
      map((goldCardBasketList) =>
        goldCardBasketList.reduce((acc, val) => {
          var basketItem: BasketElement = {
            productId: val.goldCard.id,
            productName: val.goldCard.name,
            quantity: val.amount,
          };
          return [...acc, basketItem];
        }, [])
      )
    );
  }

  refreshValues() {
    console.log(this.selectedPaymentRequest.value.type);
    if (this.selectedPaymentRequest.value.type.includes("savingPlan")) return;
    this.selectedPaymentRequest?.value?.getShipmentCost(this.checkout?.value?.afterPurchaseType)?.subscribe((shipmentCost: number) => {
      this.shippingPrice.next(shipmentCost);
      this.selectedPaymentRequest.value.getTaxPrice(this.checkout.value.afterPurchaseType).subscribe((tax: number) => {
        this.selectedPaymentRequest.value.getBasketCost().subscribe((basketCost: number) => {
          this.getVaultSubscriptionCost().subscribe((vault: number) => {
            combineLatest([
              of(basketCost),
              of(shipmentCost),
              of(tax),
              of(vault),
            ]).pipe(
              map(
                ([productPrice, shippingPrice, basketTax, vaultSubsCost]: any) =>
                  productPrice + shippingPrice + vaultSubsCost
              ),
              // tap((totalPrice) => {
              //   console.log(totalPrice);
              //   this.totalPrice.next(totalPrice);
              // })
            ).subscribe(
              (totalPrice) => {
                this.totalPrice.next(totalPrice);
              }
            );
          });
        });
      });
    });
  }
}
